import { useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const styles: Record<string, CSSProperties> = {
  footer: {
    flexShrink: 0,
    padding: '12px 16px 14px',
    background: '#F7F7F8',
    borderTop: '1px solid #ECECEE',
  },
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
  },
  field: {
    flex: 1,
    minHeight: 48,
    maxHeight: 120,
    resize: 'none',
    border: '1px solid #E5E7EB',
    borderRadius: 14,
    padding: '14px 16px',
    fontSize: 14,
    fontFamily: 'inherit',
    lineHeight: 1.4,
    color: '#111827',
    background: '#FFFFFF',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  sendBtn: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    background: 'linear-gradient(135deg, #FF5F05 0%, #E84A00 100%)',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(255,95,5,0.35)',
  },
  powered: {
    margin: '10px 0 0',
    textAlign: 'center' as const,
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: 500,
  },
};

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4.5 12 19 4.5 14.5 19 11.5 13.5 4.5 12Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ChatInput({
  onSend,
  disabled,
  placeholder = 'Ask anything...',
}: ChatInputProps) {
  const [value, setValue] = useState('');

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <footer style={styles.footer}>
      <form style={styles.form} onSubmit={handleSubmit}>
        <textarea
          style={{
            ...styles.field,
            opacity: disabled ? 0.6 : 1,
          }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          aria-label="Message Avatar"
        />
        {/* MVP: text-only — mic / file / camera controls intentionally omitted */}
        <button
          type="submit"
          style={{
            ...styles.sendBtn,
            opacity: disabled || !value.trim() ? 0.45 : 1,
            cursor: disabled || !value.trim() ? 'not-allowed' : 'pointer',
          }}
          disabled={disabled || !value.trim()}
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </form>
      <p style={styles.powered}>Powered by Avatar.inc</p>
    </footer>
  );
}
